import { getOutlineControlProps } from 'utils/buttonUtils';
import type { ReactElement } from 'react';
import {
  Button,
  Group,
  Flex,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  UnstyledButton,
  useComputedColorScheme,
} from '@mantine/core';
import { IconArrowRight, IconCheck } from '@tabler/icons-react';
import type { RuleTranslate } from '../shared/constants';

export const RuleCreateSteps = ({
  step,
  furthestStep,
  disabled,
  canVisit,
  onChange,
  translate,
}: {
  step: number;
  furthestStep: number;
  disabled: boolean;
  canVisit: (step: number) => boolean;
  onChange: (step: number) => void;
  translate: RuleTranslate;
}): ReactElement => {
  const steps = ['action', 'criteria', 'outcome', 'review'];

  return (
    <SimpleGrid
      component="nav"
      cols={4}
      spacing={{ base: 0, sm: 'xs' }}
      py={{ base: 'xs', sm: 'md' }}
      px={{ base: 'xs', sm: 'lg' }}
      style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}
    >
      {steps.map((key, index) => {
        const completed =
          index < furthestStep && canVisit(index + 1) && index !== step;
        const stepDisabled = disabled || !canVisit(index);
        return (
          <UnstyledButton
            p={{ base: 4, sm: 'xs' }}
            style={{
              borderRadius: 'var(--mantine-radius-sm)',
              cursor: stepDisabled ? 'not-allowed' : undefined,
              opacity: stepDisabled ? 0.5 : 1,
              color:
                index === step
                  ? 'var(--mantine-primary-color-filled)'
                  : undefined,
            }}
            key={key}
            disabled={stepDisabled}
            aria-current={index === step ? 'step' : undefined}
            onClick={() => onChange(index)}
          >
            <Flex
              direction={{ base: 'column', sm: 'row' }}
              gap={{ base: 4, sm: 'xs' }}
              align="center"
              ta={{ base: 'center', sm: 'left' }}
            >
              <ThemeIcon
                radius="xl"
                size={28}
                variant={index === step || completed ? 'filled' : 'outline'}
                color={index === step || completed ? undefined : 'gray'}
              >
                {completed ? <IconCheck size={16} /> : index + 1}
              </ThemeIcon>
              <Stack gap={0}>
                <Text c="dimmed" size="xs" tt="uppercase">
                  {translate('pages:rules.builder.step')} {index + 1}
                </Text>
                <Text c="inherit" fw={600} size="sm">
                  {translate(`pages:rules.builder.steps.${key}`)}
                </Text>
              </Stack>
            </Flex>
          </UnstyledButton>
        );
      })}
    </SimpleGrid>
  );
};

export const RuleCreateFooter = ({
  step,
  saving,
  valid,
  canCreate,
  publish,
  invalidMessage,
  onBack,
  onNext,
  translate,
}: {
  step: number;
  saving: boolean;
  valid: boolean;
  canCreate: boolean;
  publish: boolean;
  invalidMessage?: string;
  onBack: () => void;
  onNext: () => void;
  translate: RuleTranslate;
}): ReactElement => {
  const colorScheme = useComputedColorScheme();
  return (
    <Group
      py="md"
      px="lg"
      style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}
      justify="space-between"
      gap="sm"
    >
      <Button
        {...getOutlineControlProps(colorScheme, saving)}
        color={colorScheme === 'dark' ? 'white' : 'dark'}
        disabled={saving}
        onClick={onBack}
      >
        {step === 0
          ? translate('pages:rules.builder.cancel')
          : translate('pages:rules.builder.back')}
      </Button>
      <Group gap="sm">
        <Text c="dimmed" size="xs" id="rule-step-blocker">
          {!valid &&
            (invalidMessage ||
              translate('pages:rules.builder.completeSection'))}
          {valid &&
            step === 3 &&
            !canCreate &&
            translate('pages:rules.builder.contextBlocker')}
        </Text>
        <Button
          {...getOutlineControlProps(
            colorScheme,
            saving || !valid || (step === 3 && !canCreate)
          )}
          color={colorScheme === 'dark' ? 'white' : 'dark'}
          loading={saving}
          disabled={!valid || (step === 3 && !canCreate)}
          rightSection={<IconArrowRight size={16} />}
          aria-describedby="rule-step-blocker"
          onClick={onNext}
        >
          {step === 3
            ? translate(
                `pages:rules.builder.${publish ? 'createOn' : 'saveDraft'}`
              )
            : translate('pages:rules.builder.continue')}
        </Button>
      </Group>
    </Group>
  );
};
